<template lang="pug">
//- The rows line up with the columns of the table that owns them: settings under the
//- description, count under the quantity, subtotal under the total. In a table this
//- wide, a free-standing block cannot be read across, and a sum a thousand pixels away
//- from the total it explains proves nothing.
.usage-runs(:style="{ '--usage-runs-columns': columns }")
    //- NEVER name the provider cost or the margin here: this panel is shown to the
    //- customer, and what we pay upstream is not their business. Say what the charge
    //- depends on and let the rows add up.
    p.usage-runs__intro {{ $t('Each generation is billed on its own, at what its settings cost.') }}
    //- Grouped by what actually drives the price - the settings a generation used. A
    //- busy month is hundreds of generations, and listing them explains nothing; the
    //- handful of settings combinations behind the total is the real answer to
    //- "why this amount".
    ul.usage-runs__list
        li.usage-run(v-for="group in groups" :key="group.key")
            span.usage-run__gutter
            span.usage-run__what
                span.usage-run__label {{ group.detail || $t('Standard settings') }}
                span.usage-run__detail(v-if="group.label") {{ group.label }}
            span.usage-run__state
                span.usage-run__pending(v-if="group.pending") {{ $t('not yet settled') }}
            span.usage-run__count {{ group.count }}
            span.usage-run__each {{ group.eachAmount === null ? '' : $options.filters.moneyFormat(group.eachAmount, 'EUR') }}
            span.usage-run__amount {{ group.amount | moneyFormat('EUR') }}
    //- The listed groups must visibly add up to the line total. Usage booked before the
    //- per-generation record existed has none, so the difference is named rather than
    //- left as an unexplained gap between the list and the total.
    .usage-run.usage-run--remainder(v-if="unlistedAmount > 0")
        span.usage-run__gutter
        span.usage-run__what
            span.usage-run__label {{ $t('Earlier usage without a per-generation record') }}
        span.usage-run__state
        span.usage-run__count
        span.usage-run__each
        span.usage-run__amount {{ unlistedAmount | moneyFormat('EUR') }}
    //- The listed runs can also exceed the total, when a provisional estimate
    //- settles lower or a generation was credited. Naming that difference keeps
    //- the column adding up instead of showing more than the sum beneath it.
    .usage-run.usage-run--remainder(v-if="settledDifference < 0")
        span.usage-run__gutter
        span.usage-run__what
            span.usage-run__label {{ $t('Correction after final settlement') }}
        span.usage-run__state
        span.usage-run__count
        span.usage-run__each
        span.usage-run__amount {{ settledDifference | moneyFormat('EUR') }}
    .usage-run.usage-run--sum
        span.usage-run__gutter
        span.usage-run__what
            span.usage-run__label {{ $t('Sum') }}
        span.usage-run__state
        span.usage-run__count
        span.usage-run__each
        span.usage-run__amount {{ lineItem.total_price | moneyFormat('EUR') }}
</template>

<i18n>
{
    "de": {
        "Each generation is billed on its own, at what its settings cost.": "Jede Generierung wird einzeln berechnet, zum Preis ihrer gewählten Einstellungen.",
        "Standard settings": "Standardeinstellungen",
        "not yet settled": "noch nicht abgerechnet",
        "Earlier usage without a per-generation record": "Frühere Nutzung ohne Einzelnachweis",
        "Correction after final settlement": "Korrektur nach endgültiger Abrechnung",
        "Sum": "Summe"
    }
}
</i18n>

<script>
/**
 * The per-run breakdown behind a metered line item, shown when its row is expanded.
 *
 * A metered total is the SUM of individual generations, not quantity x unit price —
 * so the only honest way to present it is to break it down and let the parts add up.
 * The rows are grouped by the settings that drove the price, because a period can hold
 * hundreds of generations and the settings, not the individual runs, are the answer.
 * Shared by the customer billing page and both super-user period tables, so the three
 * cannot drift apart.
 */
export default {
    name: 'UsageRunsBreakdown',
    props: {
        lineItem: {
            type: Object,
            required: true,
        },
        // The grid columns of the host table, so the breakdown lands under the right
        // headings. Each table knows its own widths; the default matches the super-user
        // period tables, which share one layout.
        columns: {
            type: String,
            default: '36px minmax(0, 1fr) 150px 120px 120px 120px',
        },
    },
    computed: {
        runs() {
            return this.lineItem.usage_runs || [];
        },
        // One row per distinct SETTINGS combination - the dimension that drives the
        // price - never per generation and never per template label. A busy month is
        // hundreds of generations under a handful of settings; labels vary per run and
        // would explode the list right back into one row each. `pending` stays in the
        // key: a provisional charge must never be folded into a settled one.
        groups() {
            const grouped = new Map();

            this.runs.forEach((run) => {
                const key = [run.detail || '', run.pending ? 'pending' : 'settled'].join('|');

                if (!grouped.has(key)) {
                    grouped.set(key, {
                        key: key,
                        detail: run.detail,
                        pending: !!run.pending,
                        count: 0,
                        amount: 0,
                        amounts: [],
                        labels: new Set(),
                    });
                }

                const group = grouped.get(key);
                const amount = parseFloat(run.amount) || 0;

                group.count += 1;
                group.amount += amount;
                group.amounts.push(amount);
                if (run.label) group.labels.add(run.label);
            });

            return Array.from(grouped.values())
                .map((group) => Object.assign(group, {
                    eachAmount: this.uniformAmount(group),
                    // The template/option name is context, not a grouping dimension: it
                    // is only shown when every generation in the group shares it.
                    label: group.labels.size === 1 ? group.labels.values().next().value : null,
                }))
                .sort((first, second) => second.amount - first.amount);
        },
        // Part of the total that no listed run accounts for. Usage booked before the
        // per-run ledger existed carries no rows, so without naming this difference the
        // breakdown would simply not add up to the total above it.
        reconciliationDifference() {
            const listed = this.runs.reduce((sum, run) => sum + (parseFloat(run.amount) || 0), 0);

            return (parseFloat(this.lineItem.total_price) || 0) - listed;
        },
        unlistedAmount() {
            // Sub-cent noise is rounding, not a missing run.
            return this.reconciliationDifference > 0.005 ? this.reconciliationDifference : 0;
        },
        // Negative remainder: the listed runs add up to MORE than the line total.
        settledDifference() {
            return this.reconciliationDifference < -0.005 ? this.reconciliationDifference : 0;
        },
    },
    methods: {
        // Only worth showing when every generation in the group really did cost the
        // same. Some models price on prompt complexity too, so identical settings can
        // still differ - printing an average there would invent a per-unit price.
        uniformAmount(group) {
            if (group.count < 2) {
                return null;
            }

            const first = group.amounts[0];
            const uniform = group.amounts.every((amount) => Math.abs(amount - first) < 0.005);

            return uniform ? first : null;
        },
    },
};
</script>

<style lang="sass" scoped>
// Element's expanded cell has no horizontal padding and starts at the row's own left
// edge, so these columns sit exactly under the parent's.
.usage-runs
    padding: 0.35rem 0 0.6rem

    &__intro
        margin: 0 0 0.5rem
        padding: 0 10px 0 3.1rem
        font-size: 0.8rem
        line-height: 1.45
        color: #6b7280

    &__list
        margin: 0
        padding: 0
        list-style: none

.usage-run
    display: grid
    grid-template-columns: var(--usage-runs-columns)
    align-items: baseline
    padding: 0.3rem 0
    font-size: 0.82rem
    border-bottom: 1px solid #f1f2f4

    &:last-child
        border-bottom: 0

    > span
        padding: 0 10px
        min-width: 0

    &__gutter
        padding: 0

    &__what
        display: flex
        flex-wrap: wrap
        align-items: baseline
        gap: 0.45rem
        // Indented against the line item label above, so the rows read as its parts.
        // Margin, not padding: `.usage-run > span` sets the padding and outranks this.
        margin-left: 1.1rem

    &__label
        font-weight: 500
        color: #303133

    &__detail
        color: #9ca3af

    &__pending
        font-size: 0.72rem
        color: #b45309
        background: #fef3c7
        border-radius: 3px
        padding: 0.05rem 0.35rem

    // Count, unit price and subtotal are right-aligned like the columns they sit under,
    // so the figures form one column with the row above.
    &__count,
    &__each,
    &__amount
        text-align: right
        font-variant-numeric: tabular-nums
        white-space: nowrap

    &__each
        color: #9ca3af

    &__amount
        font-weight: 600

    &--remainder
        .usage-run__label
            color: #6b7280
            font-style: italic
            font-weight: 400

    // Closes the column: the groups above visibly add up to this, directly beneath the
    // line item total.
    &--sum
        border-bottom: 0
        border-top: 1px solid #d1d5db
        margin-top: 0.15rem
        padding-top: 0.45rem

        .usage-run__label,
        .usage-run__amount
            font-weight: 700
</style>
